import Dexie, { type Table } from 'dexie';
import type { Layout, Theme } from '../renderer/index.js';
import type { TickLike } from '../renderer/tick-store.js';

/**
 * Offline-first kalıcı depo (IndexedDB / Dexie).
 *
 * Bağlantı kopunca son layout + theme + tick'lerle stale render yapılır.
 * Memory: decision_pwa_resilience.md §2 (offline cache), §9 (boot)
 */

export interface LayoutRecord {
    id: 'current';
    document: Layout;
    updated_at: number;
}

export interface ThemeRecord {
    id: 'current';
    document: Theme;
    updated_at: number;
}

export interface LastTick {
    symbol: string;
    bid?: string;
    ask?: string;
    server_ts?: number;
    received_at: number;
}

export interface MetaRow {
    key: string;
    value: unknown;
}

class FpDb extends Dexie {
    layout!: Table<LayoutRecord, 'current'>;
    theme!: Table<ThemeRecord, 'current'>;
    last_ticks!: Table<LastTick, string>;
    meta!: Table<MetaRow, string>;

    constructor() {
        super('fiyatpanom');
        this.version(1).stores({
            layout: 'id',
            theme: 'id',
            last_ticks: 'symbol',
            meta: 'key',
        });
    }
}

export const db = new FpDb();

export async function saveLayout(document: Layout): Promise<void> {
    await db.layout.put({ id: 'current', document, updated_at: Date.now() });
}

export async function saveTheme(document: Theme): Promise<void> {
    await db.theme.put({ id: 'current', document, updated_at: Date.now() });
}

export async function loadCachedState(): Promise<{ layout: Layout; theme: Theme } | null> {
    const [l, t] = await Promise.all([db.layout.get('current'), db.theme.get('current')]);
    if (!l || !t) return null;
    return { layout: l.document, theme: t.document };
}

export async function saveTick(tick: TickLike): Promise<void> {
    if (!tick.symbol) return;
    await db.last_ticks.put({
        symbol: tick.symbol,
        bid: tick.bid,
        ask: tick.ask,
        server_ts: tick.server_ts,
        received_at: Date.now(),
    });
}

export async function loadCachedTicks(): Promise<LastTick[]> {
    return db.last_ticks.toArray();
}

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
    const row = await db.meta.get(key);
    return row !== undefined ? (row.value as T) : fallback;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
    await db.meta.put({ key, value });
}

/** last_ticks prune — kayıt sayısı çok artarsa eski tick'leri sil. */
export async function pruneTicks(keepCount = 500): Promise<void> {
    const count = await db.last_ticks.count();
    if (count <= keepCount) return;
    const all = await db.last_ticks.orderBy('received_at').toArray();
    const toDelete = all.slice(0, count - keepCount).map((t) => t.symbol);
    await db.last_ticks.bulkDelete(toDelete);
}
